import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  FiExternalLink,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiX,
  FiPlus,
} from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./Links.scss";

const Links = () => {
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [isAdminOrVorstand, setIsAdminOrVorstand] = useState(false);

  // Section Edit
  const [editSectionId, setEditSectionId] = useState(null);
  const [editSectionTitle, setEditSectionTitle] = useState("");

  // Link Edit
  const [editLinkId, setEditLinkId] = useState(null);
  const [editLinkText, setEditLinkText] = useState("");
  const [editLinkUrl, setEditLinkUrl] = useState("");

  // Add Link
  const [newLinkText, setNewLinkText] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [addingLinkToSection, setAddingLinkToSection] = useState(null);

  const token = localStorage.getItem("token");

  const axiosConfig = useMemo(() => {
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, [token]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const roles = decoded.userTypes || [];
        setIsAdminOrVorstand(
          roles.includes("admin") || roles.includes("vorstand")
        );
      } catch {
        setIsAdminOrVorstand(false);
      }
    }

    const fetchSections = async () => {
      try {
        const res = await axios.get(
          "https://jugehoerig-backend.onrender.com/api/links",
          axiosConfig
        );
        setSections(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchSections();
  }, [token, axiosConfig]);

  // SECTION
  const handleEditSection = (section) => {
    setEditSectionId(section.id);
    setEditSectionTitle(section.subtitle);
  };

  const saveSectionEdit = async () => {
    if (!editSectionTitle) return alert("Titel darf nicht leer sein");
    await axios.put(
      `https://jugehoerig-backend.onrender.com/api/links/section/title/${editSectionId}`,
      { subtitle: editSectionTitle },
      axiosConfig
    );
    setEditSectionId(null);
    setEditSectionTitle("");
    refresh();
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm("Sektion wirklich löschen?")) return;
    await axios.delete(
      `https://jugehoerig-backend.onrender.com/api/links/section/${id}`,
      axiosConfig
    );
    refresh();
  };

  // LINK
  const handleEditLink = (link) => {
    setEditLinkId(link.id);
    setEditLinkText(link.text);
    setEditLinkUrl(link.url);
  };

  const saveLinkEdit = async () => {
    if (!editLinkText || !editLinkUrl) return alert("Alles ausfüllen!");
    await axios.put(
      `https://jugehoerig-backend.onrender.com/api/links/${editLinkId}`,
      { text: editLinkText, url: editLinkUrl },
      axiosConfig
    );
    setEditLinkId(null);
    refresh();
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm("Link löschen?")) return;
    await axios.delete(
      `https://jugehoerig-backend.onrender.com/api/links/${linkId}`,
      axiosConfig
    );
    refresh();
  };

  const handleAddLinkToSection = async (sectionId) => {
    if (!newLinkText || !newLinkUrl) return alert("Alles ausfüllen!");
    await axios.post(
      `https://jugehoerig-backend.onrender.com/api/links/section/${sectionId}/link`,
      { text: newLinkText, url: newLinkUrl },
      axiosConfig
    );
    setNewLinkText("");
    setNewLinkUrl("");
    setAddingLinkToSection(null);
    refresh();
  };

  const refresh = async () => {
    const res = await axios.get(
      "https://jugehoerig-backend.onrender.com/api/links",
      axiosConfig
    );
    setSections(res.data);
  };

  return (
    <div className="links-management">
      <h2>📚 Nützliche Links</h2>

      {isAdminOrVorstand && (
        <button className="plus-button" onClick={() => navigate("/create-link")}>
          <FiPlus /> Neue Sektion
        </button>
      )}

      {sections.map((section) => (
        <div key={section.id} className="section-block">
          <div className="section-header">
            {editSectionId === section.id ? (
              <>
                <input
                  value={editSectionTitle}
                  onChange={(e) => setEditSectionTitle(e.target.value)}
                />
                <button onClick={saveSectionEdit}><FiCheck /></button>
                <button onClick={() => setEditSectionId(null)}><FiX /></button>
              </>
            ) : (
              <>
                <h3>📂 {section.subtitle}</h3>
                {isAdminOrVorstand && (
                  <>
                    <button onClick={() => handleEditSection(section)}><FiEdit /></button>
                    <button onClick={() => handleDeleteSection(section.id)}><FiTrash2 /></button>
                    <button onClick={() => setAddingLinkToSection(section.id)}><FiPlus /></button>
                  </>
                )}
              </>
            )}
          </div>

          <ul>
            {section.links.map((link) => (
              <li key={link.id}>
                {editLinkId === link.id ? (
                  <>
                    <input
                      value={editLinkText}
                      onChange={(e) => setEditLinkText(e.target.value)}
                    />
                    <input
                      value={editLinkUrl}
                      onChange={(e) => setEditLinkUrl(e.target.value)}
                    />
                    <button onClick={saveLinkEdit}><FiCheck /></button>
                    <button onClick={() => setEditLinkId(null)}><FiX /></button>
                  </>
                ) : (
                  <>
                    <a href={link.url} target="_blank" rel="noreferrer">
                      <FiExternalLink /> {link.text}
                    </a>
                    {isAdminOrVorstand && (
                      <>
                        <button onClick={() => handleEditLink(link)}><FiEdit /></button>
                        <button onClick={() => handleDeleteLink(link.id)}><FiTrash2 /></button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          {addingLinkToSection === section.id && (
            <div className="add-link-form">
              <input
                placeholder="Link Text"
                value={newLinkText}
                onChange={(e) => setNewLinkText(e.target.value)}
              />
              <input
                placeholder="https://..."
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
              />
              <button onClick={() => handleAddLinkToSection(section.id)}>
                <FiCheck />
              </button>
              <button onClick={() => setAddingLinkToSection(null)}>
                <FiX />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Links;
